import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ApService } from 'src/app/shared/services/ap.service';

export interface SipWatchPhone {
  sip_user_id: string;
  sip_name: string;
  server_port: string;
  alert: boolean;
  alarm?: string;
  ptrack: string;
}

export interface SipWatchTrack {
  timestamp: number;
  sip_user_id: string;
  sip_name: string;
  method: string;
  status: string;
  message_state: string;
  device_ip: string;
  server_ip: string;
  server_port: string;
  watched: boolean;
  alert: boolean;
  flow: string;
  path: string;
  alarm: string;
  track: string;
}

@Component({
  selector: 'app-device-sipwatch-watch-list',
  templateUrl: './device-sipwatch-watch-list.component.html',
  styleUrls: ['./device-sipwatch-watch-list.component.scss']
})
export class DeviceSipwatchWatchListComponent implements OnInit, OnChanges {
  @Input() device: any[] = [];
  @ViewChild('phoneDialog', { static: true }) phoneDialog: TemplateRef<any>;
  @ViewChild('confirmDialog', { static: true }) confirmDialog: TemplateRef<any>;

  watchList: SipWatchPhone[] = [];
  tracks: SipWatchTrack[] = [];
  summary = { total: 0, ok: 0, unauthorized: 0 };
  phoneDraft: SipWatchPhone = this.emptyPhone();
  loading = false;
  mutating = false;
  error = '';
  confirmTitle = '';
  confirmMessage = '';
  confirmButtonLabel = 'Confirm';
  confirmButtonIcon = 'check';
  confirmDanger = false;
  private initialized = false;
  private phoneDialogRef: MatDialogRef<any>;
  private confirmDialogRef: MatDialogRef<any>;
  private confirmedAction: () => void;

  constructor(
    private apService: ApService,
    private dialog: MatDialog,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initialized = true;
    this.loadState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized || !changes.device || changes.device.firstChange) return;
    const previousMac = this.macFromDevice(changes.device.previousValue);
    const currentMac = this.macFromDevice(changes.device.currentValue);
    if (currentMac && currentMac !== previousMac) this.loadState();
  }

  get currentDevice(): any {
    return this.device && this.device[0] ? this.device[0] : {};
  }

  get deviceMac(): string {
    return this.currentDevice.mac || '';
  }

  get deviceName(): string {
    return this.currentDevice.name || this.deviceMac || 'Selected device';
  }

  loadState(forceRefresh = false): void {
    if (!this.deviceMac || this.loading) return;
    this.loading = true;
    this.error = '';
    const request = forceRefresh
      ? this.apService.getDeviceSipWatchList(this.deviceMac, true)
      : this.apService.getDeviceSipWatchList(this.deviceMac);
    request.subscribe(response => {
      this.watchList = Array.isArray(response.watch_list) ? response.watch_list : [];
      this.tracks = Array.isArray(response.tracks) ? response.tracks : [];
      this.summary = response.summary || this.calculateSummary();
      this.loading = false;
    }, error => {
      this.loading = false;
      this.error = this.errorMessage(error, 'Unable to load the SIPWatch Watch List.');
    });
  }

  openAddPhone(track?: SipWatchTrack): void {
    this.phoneDraft = {
      sip_user_id: track && track.sip_user_id || '',
      sip_name: track && track.sip_name || '',
      server_port: track && track.server_port || '5060',
      alert: true,
      ptrack: ''
    };
    this.openPhoneDialog();
  }

  openEditPhone(phone: SipWatchPhone): void {
    this.phoneDraft = { ...phone };
    this.openPhoneDialog();
  }

  savePhone(): void {
    const phone = {
      ...this.phoneDraft,
      sip_user_id: String(this.phoneDraft.sip_user_id || '').trim(),
      sip_name: String(this.phoneDraft.sip_name || '').trim(),
      server_port: String(this.phoneDraft.server_port || '').trim()
    };
    if (!phone.sip_user_id || !phone.sip_name || !/^\d+$/.test(phone.server_port) ||
      Number(phone.server_port) < 1 || Number(phone.server_port) > 65535) {
      this.toastr.error('Enter a SIP ID, phone name, and a server port between 1 and 65535.');
      return;
    }
    const action = phone.ptrack ? 'update_phone' : 'add_phone';
    this.mutate({ action, phone }, () => {
      if (this.phoneDialogRef) this.phoneDialogRef.close();
      this.loadState(true);
    });
  }

  requestDeletePhone(phone: SipWatchPhone): void {
    this.requestConfirmation(
      'Remove watched phone',
      `This action will remove Phone with UserId ${phone.sip_user_id}. Do you really want to proceed?`,
      () => this.mutate({ action: 'delete_phone', phone }, () => {
        this.loadState(true);
      }),
      { buttonLabel: 'OK', buttonIcon: 'delete_outline', danger: true }
    );
  }

  requestTrackAction(action: 'clear_track' | 'reregister_track', track: SipWatchTrack): void {
    const reregister = action === 'reregister_track';
    this.requestConfirmation(
      reregister ? 'Re-register phone' : 'Clear status entry',
      reregister
        ? `Request a fresh SIP registration for ${track.sip_name || track.sip_user_id}?`
        : `Remove the current status entry for ${track.sip_name || track.sip_user_id}?`,
      () => this.runTrackAction(action, track)
    );
  }

  runTrackAction(action: 'clear_track' | 'reregister_track', track: SipWatchTrack): void {
    this.mutate({ action, track: track.track }, () => {
      if (action === 'clear_track') {
        this.tracks = this.tracks.filter(item => item.track !== track.track);
        this.summary = this.calculateSummary();
      }
    });
  }

  requestBulkAction(action: 'clear_all' | 'reregister_all'): void {
    const reregister = action === 'reregister_all';
    this.requestConfirmation(
      reregister ? 'Re-register all phones' : 'Clear all status entries',
      reregister
        ? 'Request a fresh SIP registration for every detected phone?'
        : 'Remove all current SIP registration status entries?',
      () => this.mutate({ action }, () => {
        if (action === 'clear_all') {
          this.tracks = [];
          this.summary = this.calculateSummary();
        }
      })
    );
  }

  addDetectedPhone(track: SipWatchTrack): void {
    this.openAddPhone(track);
  }

  confirmAction(): void {
    if (this.confirmDialogRef) this.confirmDialogRef.close();
    if (this.confirmedAction) this.confirmedAction();
  }

  trackDate(timestamp: number): Date | null {
    const value = Number(timestamp);
    if (!value) return null;
    return new Date(value < 1000000000000 ? value * 1000 : value);
  }

  statusClass(track: SipWatchTrack): string {
    if (track.status === '200') return 'ok';
    if (track.status === '401') return 'unauthorized';
    return 'neutral';
  }

  alarmLabel(value: string): string {
    if (value === 'alarm') return 'Alarm';
    if (value === 'ok') return 'OK';
    return '—';
  }

  trackByPhone(index: number, phone: SipWatchPhone): string {
    return phone.ptrack || `${phone.sip_user_id}-${index}`;
  }

  trackByStatus(index: number, track: SipWatchTrack): string {
    return track.track || `${track.sip_user_id}-${track.timestamp}-${index}`;
  }

  private openPhoneDialog(): void {
    this.phoneDialogRef = this.dialog.open(this.phoneDialog, {
      width: '560px',
      maxWidth: '94vw',
      disableClose: this.mutating
    });
  }

  private requestConfirmation(
    title: string,
    message: string,
    action: () => void,
    options: { buttonLabel?: string; buttonIcon?: string; danger?: boolean } = {}
  ): void {
    this.confirmTitle = title;
    this.confirmMessage = message;
    this.confirmButtonLabel = options.buttonLabel || 'Confirm';
    this.confirmButtonIcon = options.buttonIcon || 'check';
    this.confirmDanger = !!options.danger;
    this.confirmedAction = action;
    this.confirmDialogRef = this.dialog.open(this.confirmDialog, {
      width: '460px',
      maxWidth: '94vw'
    });
  }

  private mutate(payload: any, onSuccess: () => void): void {
    if (!this.deviceMac || this.mutating) return;
    this.mutating = true;
    this.apService.mutateDeviceSipWatchList(this.deviceMac, payload).subscribe(response => {
      this.mutating = false;
      onSuccess();
      this.toastr.success(response.message || 'SIPWatch Watch List updated.');
    }, error => {
      this.mutating = false;
      this.toastr.error(this.errorMessage(error, 'Unable to update the SIPWatch Watch List.'));
    });
  }

  private calculateSummary(): { total: number; ok: number; unauthorized: number } {
    return {
      total: this.tracks.length,
      ok: this.tracks.filter(track => track.status === '200').length,
      unauthorized: this.tracks.filter(track => track.status === '401').length
    };
  }

  private errorMessage(error: any, fallback: string): string {
    return error && error.error && (error.error.message || error.error.error) || error && error.message || fallback;
  }

  private emptyPhone(): SipWatchPhone {
    return { sip_user_id: '', sip_name: '', server_port: '5060', alert: true, ptrack: '' };
  }

  private macFromDevice(device: any[]): string {
    return device && device[0] && device[0].mac ? String(device[0].mac).toLowerCase() : '';
  }
}
